import { useScienceTutorSection } from './scienceTutorContext';
import { ArrowRight, CheckCircle2, ChevronDown, ChevronUp, Minus, Plus, Radiation, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { AbundanceChart, AbundanceExplore } from './AtomicModelsAbundanceExplore';
import {
  ABUNDANCE_ELEMENTS,
  BUILDER_LIMITS,
  ISOTOPE_CASES,
  ISOTOPE_DATA_SOURCES,
  SCIENCE_HALF_LIFE_TOOL_PATH,
  buildAtom,
  checkBuildTarget,
  checkRelativeAtomicMass,
  isotopeNotation,
  periodicTableMass,
  relativeAtomicMass,
  shellArrangement,
  type AtomCounts,
  type PredictionResult,
  type RamCheck,
} from './atomicModels';

const MAX_DRAWN_NUCLEONS = 40;
const NUCLEUS_SPACING = 7;
const SHELL_BASE_RADIUS = 34;
const SHELL_STEP = 18;
const VIEW_SIZE = 220;
const VIEW_CENTRE = VIEW_SIZE / 2;
const GOLDEN_ANGLE = 2.39996;

type Particle = keyof AtomCounts;

const EMPTY_COUNTS: AtomCounts = { protons: 0, neutrons: 0, electrons: 0 };

const PARTICLE_LABELS: Record<Particle, string> = {
  protons: 'Protons',
  neutrons: 'Neutrons',
  electrons: 'Electrons',
};

const PARTICLE_MAX: Record<Particle, number> = {
  protons: BUILDER_LIMITS.maxProtons,
  neutrons: BUILDER_LIMITS.maxNeutrons,
  electrons: BUILDER_LIMITS.maxProtons + BUILDER_LIMITS.maxCharge,
};

function AtomDiagram({ counts }: { counts: AtomCounts }) {
  const nucleons = counts.protons + counts.neutrons;
  const drawNucleus = nucleons <= MAX_DRAWN_NUCLEONS;
  const shells = shellArrangement(counts.electrons);
  const nucleusKinds: boolean[] = [];
  if (drawNucleus) {
    // Interleave protons (true) and neutrons (false) so both spread through the nucleus.
    for (let index = 0; index < Math.max(counts.protons, counts.neutrons); index += 1) {
      if (index < counts.protons) nucleusKinds.push(true);
      if (index < counts.neutrons) nucleusKinds.push(false);
    }
  }
  const nucleusDots = nucleusKinds.map((proton, index) => {
    const radius = NUCLEUS_SPACING * Math.sqrt(index) * 0.75;
    const angle = index * GOLDEN_ANGLE;
    return {
      key: index,
      proton,
      x: VIEW_CENTRE + radius * Math.cos(angle),
      y: VIEW_CENTRE + radius * Math.sin(angle),
    };
  });

  return (
    <svg
      viewBox={`0 0 ${VIEW_SIZE} ${VIEW_SIZE}`}
      className="mx-auto w-full max-w-[16rem]"
      role="img"
      aria-label={`Model atom: ${counts.protons} protons and ${counts.neutrons} neutrons in the nucleus, ${counts.electrons} electrons${
        shells ? ` in shells ${shells.join(', ')}` : ''
      }`}
    >
      {shells
        ? shells.map((count, shellIndex) => {
            const radius = SHELL_BASE_RADIUS + shellIndex * SHELL_STEP;
            return (
              <g key={shellIndex}>
                <circle cx={VIEW_CENTRE} cy={VIEW_CENTRE} r={radius} fill="none" stroke="currentColor" className="text-stone2" />
                {Array.from({ length: count }, (_, index) => {
                  const angle = (2 * Math.PI * index) / count - Math.PI / 2;
                  return (
                    <circle
                      key={index}
                      cx={VIEW_CENTRE + radius * Math.cos(angle)}
                      cy={VIEW_CENTRE + radius * Math.sin(angle)}
                      r="4"
                      className="fill-brand-sky"
                    />
                  );
                })}
              </g>
            );
          })
        : null}
      {drawNucleus ? (
        nucleusDots.map((dot) => (
          <circle
            key={dot.key}
            cx={dot.x}
            cy={dot.y}
            r="3.4"
            className={dot.proton ? 'fill-brand-coral' : 'fill-stone2'}
          />
        ))
      ) : (
        <g>
          <circle cx={VIEW_CENTRE} cy={VIEW_CENTRE} r="18" className="fill-wash-coral" />
          <text x={VIEW_CENTRE} y={VIEW_CENTRE - 2} textAnchor="middle" className="fill-ink text-[9px] font-extrabold">
            {counts.protons} p
          </text>
          <text x={VIEW_CENTRE} y={VIEW_CENTRE + 9} textAnchor="middle" className="fill-ink text-[9px] font-extrabold">
            {counts.neutrons} n
          </text>
        </g>
      )}
      {!shells ? (
        <text x={VIEW_CENTRE} y={VIEW_SIZE - 8} textAnchor="middle" className="fill-slate2 text-[9px] font-bold">
          Shell diagram shown for up to 20 electrons
        </text>
      ) : null}
    </svg>
  );
}

function CountControl({
  particle,
  value,
  onChange,
}: {
  particle: Particle;
  value: number;
  onChange: (value: number) => void;
}) {
  const id = `atom-${particle}`;
  return (
    <div>
      <label htmlFor={id} className="text-sm font-extrabold text-ink">
        {PARTICLE_LABELS[particle]}
      </label>
      <div className="mt-1 flex items-center gap-2">
        <button
          type="button"
          aria-label={`Remove one ${particle.slice(0, -1)}`}
          onClick={() => onChange(Math.max(0, value - 1))}
          className="rounded-full border border-hairline bg-white p-2 text-ink-soft"
        >
          <Minus className="h-4 w-4" aria-hidden="true" />
        </button>
        <input
          id={id}
          type="number"
          inputMode="numeric"
          min={0}
          max={PARTICLE_MAX[particle]}
          value={Number.isFinite(value) ? value : ''}
          onChange={(event) => onChange(event.target.value === '' ? Number.NaN : Number(event.target.value))}
          className="w-20 rounded-xl border border-hairline bg-white px-3 py-2 text-center text-sm font-extrabold text-ink"
        />
        <button
          type="button"
          aria-label={`Add one ${particle.slice(0, -1)}`}
          onClick={() => onChange(Math.min(PARTICLE_MAX[particle], (Number.isFinite(value) ? value : 0) + 1))}
          className="rounded-full border border-hairline bg-white p-2 text-ink-soft"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function FeedbackBox({ result, label }: { result: PredictionResult; label: string }) {
  return (
    <div
      role="region"
      aria-label={label}
      className={`mt-4 rounded-2xl border p-4 ${
        result.correct ? 'border-brand-mint/45 bg-wash-mint' : 'border-brand-coral/45 bg-wash-coral'
      }`}
    >
      <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
        {result.correct ? (
          <CheckCircle2 className="h-4 w-4 text-brand-mint" aria-hidden="true" />
        ) : (
          <XCircle className="h-4 w-4 text-brand-coral" aria-hidden="true" />
        )}
        {result.correct ? 'Correct' : 'Not yet'}
      </p>
      <p className="mt-1 text-sm font-medium leading-6 text-ink-soft">{result.feedback}</p>
    </div>
  );
}

function AtomBuilder() {
  const [targetId, setTargetId] = useState(ISOTOPE_CASES[0].id);
  const [counts, setCounts] = useState<AtomCounts>({ protons: 1, neutrons: 0, electrons: 1 });
  useScienceTutorSection('AtomBuilder', { targetId, counts });
  const [checkResult, setCheckResult] = useState<PredictionResult | null>(null);
  const target = ISOTOPE_CASES.find((candidate) => candidate.id === targetId) ?? ISOTOPE_CASES[0];
  const built = buildAtom(counts);
  const reviewed = built.ok ? ISOTOPE_CASES.find((candidate) => candidate.id === built.reviewedCaseId) : undefined;

  const update = (particle: Particle, value: number) => {
    setCounts((current) => ({ ...current, [particle]: value }));
    setCheckResult(null);
  };

  return (
    <section
      aria-labelledby="atom-builder-heading"
      className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8"
    >
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">Atom and ion builder</p>
      <h2 id="atom-builder-heading" className="mt-2 text-2xl font-extrabold text-ink">
        Build: {target.name}
      </h2>
      <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Choose a target">
        {ISOTOPE_CASES.map((candidate) => (
          <button
            key={candidate.id}
            type="button"
            aria-pressed={candidate.id === target.id}
            onClick={() => {
              setTargetId(candidate.id);
              setCheckResult(null);
            }}
            className={`rounded-full border px-3 py-1.5 text-xs font-extrabold ${
              candidate.id === target.id ? 'border-brand-sky bg-wash-sky text-ink' : 'border-hairline bg-white text-slate2'
            }`}
          >
            {candidate.name}
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)_minmax(0,1fr)]">
        <div className="grid content-start gap-4">
          {(Object.keys(PARTICLE_LABELS) as Particle[]).map((particle) => (
            <CountControl key={particle} particle={particle} value={counts[particle]} onChange={(value) => update(particle, value)} />
          ))}
          <button
            type="button"
            onClick={() => setCheckResult(checkBuildTarget(target, counts))}
            className="rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white"
          >
            Check my build
          </button>
        </div>

        <div className="min-w-0 rounded-[1.5rem] border border-brand-sky/25 bg-gradient-to-br from-white to-wash-sky p-4">
          <AtomDiagram counts={built.ok ? counts : EMPTY_COUNTS} />
          <p className="mt-2 flex flex-wrap justify-center gap-3 text-xs font-bold text-slate2">
            <span>● coral = proton</span>
            <span>● grey = neutron</span>
            <span>● blue = electron</span>
          </p>
        </div>

        <div className="min-w-0" aria-live="polite">
          {built.ok ? (
            <dl className="grid gap-2 text-sm font-medium text-ink-soft">
              <div className="rounded-2xl bg-wash-sunshine px-4 py-3 text-center">
                <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">Isotope notation</dt>
                <dd className="mt-1 text-3xl font-extrabold text-ink">{isotopeNotation(built)}</dd>
              </div>
              <div className="flex justify-between gap-3"><dt>Element (from protons)</dt><dd className="font-extrabold text-ink">{built.name}</dd></div>
              <div className="flex justify-between gap-3"><dt>Atomic number Z</dt><dd className="font-extrabold text-ink">{built.atomicNumber}</dd></div>
              <div className="flex justify-between gap-3"><dt>Mass number A = p + n</dt><dd className="font-extrabold text-ink">{built.massNumber}</dd></div>
              <div className="flex justify-between gap-3"><dt>Charge = p − e</dt><dd className="font-extrabold text-ink">{built.chargeLabel || '0 (neutral atom)'}</dd></div>
              <p className="mt-1 rounded-2xl bg-surface px-4 py-3 text-xs leading-5 text-slate2">
                {reviewed
                  ? `${reviewed.status === 'radioactive' ? 'Radioactive' : 'Stable'} · ${reviewed.note}`
                  : 'Not one of the reviewed cases. The counts are valid, but this tool does not say whether this nucleus exists or is stable.'}
              </p>
            </dl>
          ) : (
            <p role="alert" className="rounded-2xl border border-brand-coral/45 bg-wash-coral p-4 text-sm font-medium text-ink-soft">
              {built.reason}
            </p>
          )}
          {checkResult ? <FeedbackBox result={checkResult} label="Build feedback" /> : null}
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-3 rounded-2xl border border-brand-sunshine/50 bg-wash-sunshine p-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-start gap-2 text-sm font-medium leading-6 text-ink-soft">
          <Radiation className="mt-1 h-4 w-4 shrink-0" aria-hidden="true" />
          Carbon-14, tritium and uranium isotopes are radioactive. Decay and half-life are modelled in
          the Science Radiometric Dating &amp; Half-life Lab.
        </p>
        <Link
          to={SCIENCE_HALF_LIFE_TOOL_PATH}
          className="inline-flex shrink-0 items-center gap-2 rounded-full bg-ink px-4 py-2 text-sm font-extrabold text-white"
        >
          Open the half-life lab <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
}

function AbundanceSection() {
  const [elementId, setElementId] = useState(ABUNDANCE_ELEMENTS[0].id);
  const [answer, setAnswer] = useState('');
  useScienceTutorSection('AbundanceSection', { elementId, answer });
  const [result, setResult] = useState<RamCheck | null>(null);
  const [view, setView] = useState<'explore' | 'practice'>('explore');
  const element = ABUNDANCE_ELEMENTS.find((candidate) => candidate.id === elementId) ?? ABUNDANCE_ELEMENTS[0];
  const exact = relativeAtomicMass(element);
  const tableValue = periodicTableMass(element);
  const revealed = result !== null && result.status !== 'invalid';

  return (
    <div className="mt-5">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Choose an element">
        {ABUNDANCE_ELEMENTS.map((candidate) => (
          <button
            key={candidate.id}
            type="button"
            aria-pressed={candidate.id === element.id}
            onClick={() => {
              setElementId(candidate.id);
              setAnswer('');
              setResult(null);
            }}
            className={`rounded-full border px-3 py-1.5 text-xs font-extrabold ${
              candidate.id === element.id ? 'border-brand-sky bg-wash-sky text-ink' : 'border-hairline bg-white text-slate2'
            }`}
          >
            {candidate.name}
          </button>
        ))}
      </div>
      <div className="mt-4 inline-flex gap-1 rounded-full border border-hairline bg-canvas-pure p-1" role="tablist" aria-label="Explore or practise relative atomic mass">
        {(['explore', 'practice'] as const).map((candidate) => (
          <button
            key={candidate}
            type="button"
            role="tab"
            aria-selected={view === candidate}
            onClick={() => setView(candidate)}
            className={`rounded-full px-4 py-2 text-sm font-extrabold ${view === candidate ? 'bg-ink text-white' : 'text-slate2'}`}
          >
            {candidate === 'explore' ? 'Explore' : 'Practice'}
          </button>
        ))}
      </div>
      {view === 'explore' ? (
        <div className="mt-4">
          <AbundanceExplore key={element.id} element={element} />
        </div>
      ) : (
        <div className="mt-4 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="min-w-0">
            <div className="rounded-[1.5rem] border border-brand-sky/25 bg-white p-4">
              <AbundanceChart element={element} />
            </div>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[18rem] text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate2">
                  <tr><th className="py-1 pr-3">Isotope</th><th className="py-1 pr-3">Isotopic mass (u)</th><th className="py-1">Abundance</th></tr>
                </thead>
                <tbody className="text-ink-soft">
                  {element.isotopes.map((isotope) => (
                    <tr key={isotope.massNumber}>
                      <td className="py-1 pr-3 font-extrabold text-ink">{element.symbol}-{isotope.massNumber}</td>
                      <td className="py-1 pr-3">{isotope.isotopicMass.toFixed(4)}</td>
                      <td className="py-1">{(isotope.abundance * 100).toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="min-w-0">
            <label htmlFor="ram-answer" className="text-sm font-extrabold text-ink">
              Calculate Ar({element.symbol}) in u
            </label>
            <div className="mt-2 flex flex-wrap gap-2">
              <input
                id="ram-answer"
                type="number"
                inputMode="decimal"
                step="0.001"
                value={answer}
                onChange={(event) => {
                  setAnswer(event.target.value);
                  setResult(null);
                }}
                className="w-36 rounded-xl border border-hairline bg-white px-3 py-2 text-sm font-extrabold text-ink"
              />
              <button
                type="button"
                disabled={answer.trim() === ''}
                onClick={() => setResult(checkRelativeAtomicMass(element, Number(answer)))}
                className="rounded-full bg-ink px-5 py-2.5 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                Check Ar
              </button>
            </div>
            {result ? (
              <FeedbackBox
                label="Relative atomic mass feedback"
                result={{ correct: result.status === 'correct' || result.status === 'approximate', feedback: result.feedback }}
              />
            ) : null}
            {revealed ? (
              <div className="mt-4 rounded-2xl bg-surface p-4 text-sm font-medium leading-6 text-ink-soft">
                <p className="font-extrabold text-ink">Worked calculation</p>
                <p className="mt-1 break-words">
                  Ar = {element.isotopes.map((isotope) => `(${isotope.isotopicMass.toFixed(4)} × ${isotope.abundance})`).join(' + ')} ={' '}
                  <strong>{exact.toFixed(3)}</strong>
                </p>
                <p className="mt-2">
                  Periodic table value: <strong>{tableValue ?? 'not available'}</strong>. Small differences come from
                  rounding and from natural variation in isotopic abundance between samples.
                </p>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

export function IsotopesMode({ seniorOpenByDefault }: { seniorOpenByDefault: boolean }) {
  const [seniorOpen, setSeniorOpen] = useState(seniorOpenByDefault);
  useScienceTutorSection('IsotopesMode', { seniorOpen });
  return (
    <div className="space-y-6">
      <AtomBuilder />
      <section
        aria-labelledby="abundance-heading"
        className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Senior · Year 11 and above</p>
            <h2 id="abundance-heading" className="mt-1 text-2xl font-extrabold text-ink">
              Relative atomic mass from isotopic abundance
            </h2>
          </div>
          <button
            type="button"
            aria-expanded={seniorOpen}
            aria-controls="abundance-panel"
            onClick={() => setSeniorOpen((open) => !open)}
            className="inline-flex items-center gap-2 rounded-full border border-hairline bg-white px-4 py-2 text-sm font-extrabold text-ink-soft"
          >
            {seniorOpen ? <ChevronUp className="h-4 w-4" aria-hidden="true" /> : <ChevronDown className="h-4 w-4" aria-hidden="true" />}
            {seniorOpen ? 'Hide relative atomic mass' : 'Open relative atomic mass'}
          </button>
        </div>
        {seniorOpen ? (
          <div id="abundance-panel">
            <p className="mt-3 max-w-3xl text-sm font-medium leading-6 text-slate2">
              Ar is the abundance-weighted mean of the isotopic masses: Ar = Σ(isotopic mass × fraction).
            </p>
            <AbundanceSection />
            <p className="mt-5 text-xs font-medium leading-5 text-slate2">
              Data: {ISOTOPE_DATA_SOURCES.join(' · ')}. Pending teaching review.
            </p>
          </div>
        ) : null}
      </section>
    </div>
  );
}
